import type { ZodTypeAny, z } from 'zod';
import { apiErrorFromResponse } from './apiError.js';
import { env } from './env.js';

/**
 * Typed fetch wrapper. Every call declares the expected response schema so
 * unexpected server responses fail loudly at the boundary rather than
 * silently as runtime bugs deeper in the UI.
 *
 * `credentials: 'include'` sends the httpOnly session cookie (web flow).
 * Mobile clients will use a different wrapper that injects `Authorization`.
 */
export async function apiFetch<S extends ZodTypeAny>(
  path: string,
  schema: S,
  init?: RequestInit,
): Promise<z.infer<S>> {
  const hasBody = init?.body != null;
  const res = await fetch(`${env.VITE_API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) throw await apiErrorFromResponse(res);

  const body = await res.json();
  return schema.parse(body);
}

/**
 * Multipart POST. The browser must set `Content-Type` itself (it carries the
 * boundary), so this cannot go through `apiFetch`, which forces JSON.
 * @fastify/multipart only sees text fields parsed BEFORE the file part —
 * append fields first, the file last.
 */
export async function apiFetchForm<S extends ZodTypeAny>(
  path: string,
  schema: S,
  form: FormData,
  init?: Omit<RequestInit, 'body'>,
): Promise<z.infer<S>> {
  const res = await fetch(`${env.VITE_API_URL}${path}`, {
    method: 'POST',
    ...init,
    body: form,
    credentials: 'include',
  });
  if (!res.ok) throw await apiErrorFromResponse(res);
  return schema.parse(await res.json());
}

/**
 * A request whose success carries no body (`204 No Content`), e.g. a DELETE.
 * `apiFetch` would fail on the empty body; this only checks the status.
 */
export async function apiFetchNoContent(path: string, init?: RequestInit): Promise<void> {
  const hasBody = init?.body != null;
  const res = await fetch(`${env.VITE_API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) throw await apiErrorFromResponse(res);
}
